import React, { useRef, useState } from 'react'
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import AdmissionLayout from './AdmissionLayout'
import ApplicantForm from './ApplicantForm'
import { createApplicant, getApplicantByIdNumber } from './api/admissionApi'
import { prefillFromStudent } from './fields'
import { useLoading } from '../contexts/LoadingContext'

// รับสมัครผู้สมัครใหม่
//
// ระบบเดิมแยกเป็น 4 หน้า (registerMatthayom / registerPrathom / registerOldMatthayom /
// registerOldPrathom) ที่มีฟอร์มเกือบเหมือนกันคนละก๊อป ที่นี่เหลือหน้าเดียว:
// ระดับชั้นเลือกในฟอร์ม ส่วน "นักเรียนเก่า" คือติ๊กช่องเดียวเพื่อกรอกเลขประจำตัวเดิม

// สีของผลค้นระเบียนเดิม — "ไม่เจอ" ไม่ใช่สีแดง เพราะไม่ใช่ความผิดพลาดและบันทึกต่อได้
const LOOKUP_TONE = {
  found: { cls: 'text-success', icon: 'ri-check-line' },
  none:  { cls: 'text-warning', icon: 'ri-information-line' },
  error: { cls: 'text-danger',  icon: 'ri-error-warning-line' },
}

export default function AdmissionRegister() {
  const navigate = useNavigate()
  const { setLoading } = useLoading()
  const [submitting, setSubmitting] = useState(false)
  const [isReturning, setIsReturning] = useState(false)
  const [existingIdNumber, setExistingIdNumber] = useState('')

  const [prefill, setPrefill] = useState(null)
  const [lookup, setLookup] = useState(null)     // ผลค้นระเบียนเดิม { tone, text }
  const [lookingUp, setLookingUp] = useState(false)
  const lastLookup = useRef('')

  // ดึงระเบียนเดิมมาเติมฟอร์มตอน cursor ออกจากช่องเลขประจำตัวเดิม
  // ทำตอน blur ไม่ใช่ระหว่างพิมพ์ เพราะเลขระหว่างพิมพ์เป็นเลขของคนอื่นได้ตลอด
  // (พิมพ์ 24501 จะผ่าน 2, 24, 245, 2450 ซึ่งล้วนเป็นเลขที่มีเจ้าของ)
  const lookupExisting = async () => {
    const idnumber = existingIdNumber.trim()
    if (!idnumber || idnumber === lastLookup.current) return
    lastLookup.current = idnumber

    setLookingUp(true)
    try {
      const row = await getApplicantByIdNumber(idnumber)
      setPrefill(prefillFromStudent(row))
      setLookup({ tone: 'found', text: `พบ ${row.st_name} — เติมข้อมูลเดิมให้แล้ว ตรวจสอบและแก้ไขได้ตามจริง` })
    } catch (err) {
      setPrefill(null)
      // ไม่เจอ ไม่ใช่ความผิดพลาด — นักเรียนเก่าที่สมัครด้วยกระดาษไม่เคยถูกคีย์เข้าระบบ
      // มีแต่เลขประจำตัวติดตัวมา บันทึกได้ตามปกติโดยใช้เลขที่กรอก แค่ไม่มีอะไรให้เติมให้
      setLookup(err.response?.status === 404
        ? { tone: 'none', text: `ยังไม่มีระเบียนของเลข ${idnumber} ในระบบ — กรอกฟอร์มเองได้ ระบบจะบันทึกโดยใช้เลขนี้` }
        : { tone: 'error', text: 'ค้นหาระเบียนเดิมไม่สำเร็จ กรอกฟอร์มเองได้ตามปกติ' })
    } finally {
      setLookingUp(false)
    }
  }

  const toggleReturning = (checked) => {
    setIsReturning(checked)
    if (!checked) {
      setExistingIdNumber('')
      setPrefill(null)
      setLookup(null)
      lastLookup.current = ''
    }
  }

  const handleSubmit = async (payload) => {
    if (isReturning && !existingIdNumber.trim()) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้กรอกเลขประจำตัวเดิม' })
      return
    }

    setSubmitting(true)
    setLoading(true)
    try {
      const body = { ...payload }
      if (isReturning) body.existingIdNumber = existingIdNumber.trim()

      const res = await createApplicant(body)
      const next = await Swal.fire({
        icon: 'success',
        title: 'รับสมัครสำเร็จ',
        html: `เลขประจำตัว <strong>${res.st_idnumber}</strong>`,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'พิมพ์ใบมอบตัว',
        denyButtonText: 'รับสมัครรายถัดไป',
        cancelButtonText: 'ไปหน้ารายชื่อ',
      })

      if (next.isConfirmed) navigate(`/admission/print/${res.st_id}`)
      else if (next.isDenied) window.location.reload()
      else navigate('/admission/applicants')
    } catch (err) {
      const data = err.response?.data
      Swal.fire({
        icon: 'error',
        title: data?.title || 'บันทึกไม่สำเร็จ',
        text: data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่',
      })
    } finally {
      setSubmitting(false)
      setLoading(false)
    }
  }

  return (
    <AdmissionLayout title="รับสมัครนักเรียน" breadcrumb={[{ label: 'รับสมัครนักเรียน' }]}>
      <Card className="card-one mb-3">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={5}>
              <Form.Check
                type="switch"
                id="returning-student"
                label="เป็นนักเรียนเก่าที่มีเลขประจำตัวอยู่แล้ว"
                checked={isReturning}
                disabled={submitting}
                onChange={(e) => toggleReturning(e.target.checked)}
              />
              <div className="fs-xs text-secondary mt-1">
                เช่น นักเรียน ม.3 ของโรงเรียนที่สมัครเรียนต่อ ม.4 — ใช้เลขประจำตัวเดิม ไม่ออกเลขใหม่
                <br />ถ้าเป็นนักเรียนเก่าที่ไม่เคยถูกคีย์เข้าระบบ (สมัครด้วยกระดาษ) ก็เปิดสวิตช์นี้ได้ตามปกติ
              </div>
            </Col>
            {isReturning && (
              <Col md={7}>
                <Form.Label className="fs-sm mb-1">เลขประจำตัวเดิม</Form.Label>
                <Form.Control
                  value={existingIdNumber}
                  disabled={submitting}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="เช่น 24501"
                  onChange={(e) => setExistingIdNumber(e.target.value.replace(/\D/g, ''))}
                  onBlur={lookupExisting}
                />
                {lookingUp && (
                  <div className="fs-xs text-secondary mt-1">
                    <Spinner animation="border" size="sm" className="me-1" />กำลังค้นระเบียนเดิม…
                  </div>
                )}
                {!lookingUp && lookup && (
                  <div className={`fs-xs mt-1 ${LOOKUP_TONE[lookup.tone].cls}`}>
                    <i className={`me-1 ${LOOKUP_TONE[lookup.tone].icon}`} />
                    {lookup.text}
                  </div>
                )}
                {!lookingUp && !lookup && (
                  <div className="fs-xs text-secondary mt-1">
                    กรอกแล้วกด Tab หรือคลิกที่อื่น ระบบจะดึงข้อมูลเดิมมาให้
                  </div>
                )}
              </Col>
            )}
            {!isReturning && (
              <Col md={7} className="text-md-end mt-3 mt-md-0">
                <span className="fs-sm text-secondary">
                  <i className="ri-information-line me-1" />
                  ระบบจะออกเลขประจำตัวให้อัตโนมัติเมื่อกดบันทึก
                </span>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <ApplicantForm
        mode="create"
        prefill={prefill}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/admission/applicants')}
      />
    </AdmissionLayout>
  )
}
